<template>
  <Teleport to="body">
    <div v-if="needsBanner" class="cookie-banner" role="dialog" :aria-label="tr('Cookie consent')">
      <div class="cookie-card">
        <div class="cookie-copy">
          <p>
            {{ tr("We use cookies to run Workspace369 and, with your consent, to measure usage and advertising. Details in our") }}
            <NuxtLink :to="localePath('/cookie-policy')">{{ tr("Cookie Policy") }}</NuxtLink>.
          </p>
        </div>
        <div class="cookie-actions">
          <button type="button" class="cookie-accept" @click="acceptAll">{{ tr("Accept all") }}</button>
          <button type="button" class="cookie-reject" @click="rejectNonEssential">{{ tr("Reject") }}</button>
          <button type="button" class="cookie-customize" @click="openPreferences">
            {{ tr("Customize") }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="preferencesOpen" class="cookie-modal-backdrop" @click.self="closePreferences">
      <div
        ref="modalRef"
        class="cookie-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-heading"
      >
        <div class="cookie-modal-head">
          <div>
            <strong id="cookie-modal-heading">{{ tr("Cookie preferences") }}</strong>
            <p>{{ tr("Choose what loads on this site. Necessary cookies always run.") }}</p>
          </div>
          <button ref="closeButtonRef" type="button" :aria-label="tr('Close')" @click="closePreferences">&times;</button>
        </div>

        <div class="cookie-option">
          <div>
            <strong>{{ tr("Strictly necessary") }} <span class="cookie-locked">{{ tr("Always on") }}</span></strong>
            <p>{{ tr("Sign-in, security, locale, and your saved consent choice.") }}</p>
          </div>
          <span class="cookie-switch is-on" aria-hidden="true"><i></i></span>
        </div>

        <label class="cookie-option">
          <div>
            <strong>{{ tr("Analytics") }}</strong>
            <p>{{ tr("Anonymous usage statistics: Google Analytics, Microsoft Clarity, Ahrefs.") }}</p>
          </div>
          <input v-model="prefs.analytics" type="checkbox" class="cookie-switch-input" />
          <span class="cookie-switch" aria-hidden="true"><i></i></span>
        </label>

        <label class="cookie-option">
          <div>
            <strong>{{ tr("Marketing") }}</strong>
            <p>{{ tr("Advertising measurement (Google Ads, Meta) and Rewardful affiliate tracking.") }}</p>
          </div>
          <input
            v-model="prefs.marketing"
            type="checkbox"
            class="cookie-switch-input"
            :disabled="gpc"
            :aria-describedby="gpc ? 'cookie-gpc-note' : undefined"
          />
          <span class="cookie-switch" aria-hidden="true"><i></i></span>
        </label>

        <p v-if="gpc" id="cookie-gpc-note" class="cookie-gpc">
          {{ tr("Your browser sends a Global Privacy Control signal, so marketing cookies stay off.") }}
        </p>
        <button type="button" class="cookie-accept cookie-save" @click="saveAndClose">
          {{ tr("Save preferences") }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
const {
  consent,
  gpc,
  needsBanner,
  preferencesOpen,
  acceptAll,
  rejectNonEssential,
  save,
  openPreferences,
  closePreferences,
} = useCookieConsent();
const { localePath } = useWorkspaceLocale();
const { tr } = useHomeI18n();

const prefs = reactive({ analytics: false, marketing: false });

const modalRef = ref(null);
const closeButtonRef = ref(null);
let previouslyFocused = null;

const focusableSelector =
  'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

// Escape closes, Tab/Shift+Tab stay trapped inside the modal. Registered on
// document (matching DemoRequestDialog's dialog pattern) so it works regardless
// of which element inside the modal currently holds focus.
const handleModalKeydown = (event) => {
  if (!preferencesOpen.value || !modalRef.value) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closePreferences();
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = Array.from(modalRef.value.querySelectorAll(focusableSelector));
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  // Focus landed outside the modal entirely (e.g. a click on non-focusable
  // padding/copy) — pull it back in rather than letting Tab carry it further out.
  if (!modalRef.value.contains(document.activeElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

// immediate: true covers a modal that's already open when this component mounts
// (e.g. re-teleported after a navigation), not just the open transition.
watch(
  preferencesOpen,
  (open, wasOpen) => {
    if (!open) {
      // Only a real close restores focus. The immediate first run (closed on
      // mount) must not, or every page load focuses the footer button.
      if (!wasOpen) return;
      // nextTick: closing can coincide with the banner itself unmounting (Save
      // after Customize — decide() flips both consent.decided and
      // preferencesOpen in the same tick), which removes the saved "Customize"
      // button from the DOM. Wait for that DOM patch to land before checking
      // whether the saved element is still connected, and fall back to the
      // footer's "Your privacy choices" button when it isn't. That fallback is
      // matched by data-privacy-choices-trigger, not aria-haspopup="dialog" —
      // the header's (below-lg-hidden) LanguageSelector carries that attribute
      // too and would otherwise win the query. preventScroll on both branches:
      // the footer button sits far down the page and a plain .focus() would
      // jump the viewport there.
      const restoreTo = previouslyFocused;
      previouslyFocused = null;
      nextTick(() => {
        if (restoreTo && document.contains(restoreTo)) {
          restoreTo.focus({ preventScroll: true });
        } else {
          document.querySelector('[data-privacy-choices-trigger]')?.focus({ preventScroll: true });
        }
      });
      return;
    }
    prefs.analytics = consent.value?.analytics ?? false;
    prefs.marketing = gpc.value ? false : (consent.value?.marketing ?? false);
    previouslyFocused = document.activeElement;
    nextTick(() => closeButtonRef.value?.focus());
  },
  { immediate: true },
);

onMounted(() => {
  document.addEventListener('keydown', handleModalKeydown);
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleModalKeydown);
});

const saveAndClose = () => save(prefs);
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 1rem;
  left: 1rem;
  z-index: 90;
  display: flex;
  justify-content: flex-start;
  pointer-events: none;
}

.cookie-card {
  display: grid;
  gap: 0.7rem;
  width: min(calc(100vw - 2rem), 19.5rem);
  border: 1px solid rgb(148 163 184 / 0.18);
  border-radius: 0.6rem;
  background: rgb(15 23 42 / 0.92);
  box-shadow: 0 12px 36px rgb(2 6 23 / 0.32);
  padding: 0.8rem 0.9rem;
  pointer-events: auto;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.cookie-copy p {
  color: #cbd5e1;
  font-size: 0.73rem;
  line-height: 1.5;
}

.cookie-copy a {
  color: #67e8f9;
  text-decoration: underline;
}

.cookie-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.35rem;
}

.cookie-actions button,
.cookie-modal-actions button {
  min-height: 2rem;
  border-radius: 0.4rem;
  padding: 0.35rem 0.75rem;
  font-size: 0.72rem;
  font-weight: 800;
  transition: background-color 160ms ease, border-color 160ms ease;
}

.cookie-accept {
  background: #0e7490;
  color: #fff;
}

.cookie-accept:hover {
  background: #155e75;
}

/* Reject matches Accept all in colour, weight and size: EU regulators (CNIL, the German DPAs)
   expect refusing to be as easy and as visible as accepting. Equal widths whatever the label length. */
.cookie-accept:not(.cookie-save),
.cookie-reject {
  flex: 1 1 0;
}

/* Customize gets its own line, so the two choices share the full width. */
.cookie-customize {
  flex-basis: 100%;
  text-align: left;
}

.cookie-reject {
  background: #0e7490;
  color: #fff;
}

.cookie-reject:hover {
  background: #155e75;
}

.cookie-customize {
  color: #94a3b8;
}

.cookie-customize:hover {
  color: #e2e8f0;
}

.cookie-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(2 6 23 / 0.55);
  padding: 1rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.cookie-modal {
  display: grid;
  gap: 0.75rem;
  width: min(100%, 26rem);
  border: 1px solid rgb(148 163 184 / 0.18);
  border-radius: 10px;
  background: rgb(15 23 42 / 0.96);
  box-shadow: 0 24px 70px rgb(2 6 23 / 0.5);
  padding: 1.1rem;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.cookie-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.25rem;
}

.cookie-modal-head strong {
  color: #fff;
  font-size: 0.95rem;
  font-weight: 850;
}

.cookie-modal-head p {
  margin-top: 0.25rem;
  color: #94a3b8;
  font-size: 0.75rem;
  line-height: 1.5;
}

.cookie-modal-head button {
  color: #94a3b8;
  font-size: 1.25rem;
  line-height: 1;
  transition: color 160ms ease;
}

.cookie-modal-head button:hover {
  color: #e2e8f0;
}

.cookie-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid rgb(148 163 184 / 0.15);
  border-radius: 0.55rem;
  padding: 0.75rem 0.85rem;
  transition: border-color 160ms ease;
}

label.cookie-option {
  cursor: pointer;
}

label.cookie-option:hover {
  border-color: rgb(27 167 206 / 0.4);
}

.cookie-option strong {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #e2e8f0;
  font-size: 0.84rem;
  font-weight: 800;
}

.cookie-option p {
  margin-top: 0.25rem;
  color: #94a3b8;
  font-size: 0.73rem;
  line-height: 1.5;
}

.cookie-locked {
  border-radius: 4px;
  background: rgb(27 167 206 / 0.15);
  padding: 0.2rem 0.55rem;
  color: #67e8f9;
  font-size: 0.62rem;
  font-weight: 800;
  text-transform: uppercase;
}

.cookie-switch {
  position: relative;
  flex: none;
  width: 2.1rem;
  height: 1.15rem;
  border-radius: 999px;
  background: #334155;
  transition: background-color 180ms ease;
}

.cookie-switch i {
  position: absolute;
  top: 0.14rem;
  left: 0.14rem;
  height: 0.87rem;
  width: 0.87rem;
  border-radius: 999px;
  background: #e2e8f0;
  transition: transform 180ms ease, background-color 180ms ease;
}

.cookie-switch.is-on {
  background: #1ba7ce;
}

.cookie-switch.is-on i {
  transform: translateX(0.95rem);
  background: #fff;
}

.cookie-switch-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.cookie-switch-input:checked + .cookie-switch {
  background: #1ba7ce;
}

.cookie-switch-input:checked + .cookie-switch i {
  transform: translateX(0.95rem);
  background: #fff;
}

.cookie-switch-input:focus-visible + .cookie-switch {
  outline: 2px solid #67e8f9;
  outline-offset: 2px;
}

.cookie-gpc {
  color: #94a3b8;
  font-size: 0.72rem;
  line-height: 1.5;
}

.cookie-save {
  width: 100%;
  min-height: 2.4rem;
  margin-top: 0.25rem;
  border-radius: 0.45rem;
  font-size: 0.8rem;
}

@media (max-width: 640px) {
  .cookie-modal-backdrop {
    align-items: flex-end;
    padding: 0.5rem;
  }

  .cookie-modal {
    width: 100%;
  }
}
</style>
